import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { NamedFolder, ResponseWrapper } from 'ui/toast';
import { SelectionTypes } from 'utils/contexts/selection';
import {
  addNegativeToast,
  addNegativeToastWithErrorId,
  addPositiveToast,
} from 'modules/toastContainer';
import keyBy from 'lodash/keyBy';
import { useDeleteAssetsMutation } from './useDeleteAssetsMutation';

enum KnownDeleteFailures {
  FolderNotFoundFailure = 'FolderNotFoundFailure',
  CannotDeleteRootFolderFailure = 'CannotDeleteRootFolderFailure',
  CannotDeleteFolderWithWhatsAppContents = 'CannotDeleteFolderWithWhatsAppContents',
}

const displayText = {
  genericError: 'Could not delete items',
};

export function useDeleteAssetContentsAndFolders() {
  const dispatch = useDispatch();
  const { mutate, isLoading } = useDeleteAssetsMutation();

  const deleteItems = useCallback(
    async ({
      itemsToDelete,
    }: {
      itemsToDelete: { id: number; name: string; type: SelectionTypes }[];
    }) => {
      const folderIdsToDelete = itemsToDelete
        .filter((item) => item.type === SelectionTypes.Folder)
        .map((item) => item.id);
      const contentIdsToDelete = itemsToDelete
        .filter((item) => item.type === SelectionTypes.Content)
        .map((item) => item.id);
      const itemsById = keyBy(itemsToDelete, 'id');

      if (!contentIdsToDelete?.length && !folderIdsToDelete?.length) {
        return dispatch(addNegativeToast('No items selected to delete'));
      }

      try {
        const { errors, data } = await mutate({
          variables: { folderIds: folderIdsToDelete, contentIds: contentIdsToDelete },
        });

        if (!data && !!errors?.length) {
          dispatch(addNegativeToast('An unknown error occurred'));
        }

        data?.deleteAssetContentsAndFolders.folderFailures.forEach((err) => {
          switch (err.__typename) {
            case KnownDeleteFailures.FolderNotFoundFailure: {
              dispatch(
                addNegativeToast(
                  <ResponseWrapper>
                    <NamedFolder name={itemsById[err.id]?.name || ''} /> item can not be deleted as{' '}
                    <NamedFolder name={itemsById[err.id]?.name || ''} /> does not exist.
                  </ResponseWrapper>,
                ),
              );
              break;
            }
            case KnownDeleteFailures.CannotDeleteRootFolderFailure: {
              dispatch(
                addNegativeToast(
                  <ResponseWrapper>
                    <NamedFolder name={itemsById[err.id]?.name || ''} /> item can not be deleted as{' '}
                    <NamedFolder name={itemsById[err.id]?.name || ''} /> it's the root folder.
                  </ResponseWrapper>,
                ),
              );
              break;
            }

            case KnownDeleteFailures.CannotDeleteFolderWithWhatsAppContents: {
              dispatch(
                addNegativeToast(
                  <ResponseWrapper>
                    <NamedFolder name={itemsById[err.id]?.name || ''} /> item can not be deleted as{' '}
                    <NamedFolder name={itemsById[err.id]?.name || ''} /> because includes
                    WhatsAppContents.
                  </ResponseWrapper>,
                ),
              );
              break;
            }

            default: {
              dispatch(addNegativeToast('Item could not be deleted.'));
            }
          }
        });

        data?.deleteAssetContentsAndFolders.deletedContent?.forEach((content) => {
          switch (itemsById[content.id]?.type) {
            case SelectionTypes.Content: {
              dispatch(
                addPositiveToast(
                  <ResponseWrapper>
                    Asset <strong>{content.name}</strong> was successfully deleted.
                  </ResponseWrapper>,
                ),
              );
              break;
            }

            case SelectionTypes.Folder: {
              dispatch(
                addPositiveToast(
                  <ResponseWrapper>
                    Folder <strong>{content.name}</strong> was successfully deleted.
                  </ResponseWrapper>,
                ),
              );
              break;
            }

            default: {
              console.error('Deleted an unallowed content item: possible type mismatch'); // eslint-disable-line
            }
          }
        });

        return data?.deleteAssetContentsAndFolders;
      } catch (error) {
        return dispatch(
          addNegativeToastWithErrorId(displayText.genericError, 'deleteAssetContentsAndFolders'),
        );
      }
    },
    [mutate],
  );

  return { deleteItems, isLoading };
}
